import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Package, Palette, Search, Camera, Link, Wand2, Check, Star, AlertCircle, X, ChevronDown, Mail } from 'lucide-react';
import { usePricing } from '../config/pricing';
import AppFooter from '../components/AppFooter';
import MagicCodeSignIn from '../components/MagicCodeSignIn';
import { BACKEND_URL } from '../config/api';

const LandingPage = () => {
  const { plans, loading } = usePricing();
  const [authError, setAuthError] = useState(null);
  const [showSignInMenu, setShowSignInMenu] = useState(false);
  const menuRef = useRef(null);

  // Check for auth errors in URL params (from failed Google OAuth)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('auth_error');
    if (error) {
      setAuthError(error.replace(/_/g, ' '));
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowSignInMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleGoogleSignIn = () => {
    const origin = window.location.origin;
    window.location.href = `${BACKEND_URL}/api/auth/google/login?origin=${encodeURIComponent(origin)}`;
  };

  const handleFacebookSignIn = () => {
    const origin = window.location.origin;
    window.location.href = `${BACKEND_URL}/api/auth/facebook/login?origin=${encodeURIComponent(origin)}`;
  };

  // Use pricing from API (or defaults while loading)
  const allTiers = plans;

  return (
    <div className="min-h-screen bg-cream">
      {/* Auth Error Banner */}
      {authError && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle size={16} />
              <span>Sign in failed: {authError}. Please try again.</span>
            </div>
            <button onClick={() => setAuthError(null)} className="text-red-400 hover:text-red-600">
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      {/* Navigation */}
      <nav className="bg-white border-b border-beige">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Sparkles className="h-6 w-6 text-sage" />
              <div className="flex flex-col">
                <span className="text-xl font-light text-charcoal leading-tight">Kura Stash</span>
                <span className="text-xs text-gray-soft">from <a href="https://kura.shop" target="_blank" rel="noopener noreferrer" className="text-sage hover:text-sage-dark underline">Kura Studio</a></span>
              </div>
            </div>
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowSignInMenu(!showSignInMenu)}
                data-testid="nav-sign-in-btn"
                className="bg-sage text-white px-6 py-2.5 rounded-lg font-medium hover:bg-sage-dark hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center gap-1.5"
              >
                Sign In
                <ChevronDown size={16} className={`transition-transform duration-200 ${showSignInMenu ? 'rotate-180' : ''}`} />
              </button>
              {showSignInMenu && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl border border-beige shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-4 py-2.5 border-b border-beige">
                    <p className="text-xs text-gray-soft">Continue with</p>
                  </div>
                  <button
                    onClick={handleGoogleSignIn}
                    data-testid="sign-in-google-btn"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-charcoal hover:bg-sage/5 transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    <span>Google</span>
                  </button>
                  <button
                    onClick={handleFacebookSignIn}
                    data-testid="sign-in-facebook-btn"
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-charcoal hover:bg-sage/5 transition-colors"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <span>Facebook</span>
                  </button>
                  <div className="border-t border-beige px-4 py-3">
                    <MagicCodeSignIn onSuccess={() => { window.location.href = '/stash'; }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-charcoal mb-6 leading-tight">
            Discover sophisticated organization that inspires creativity
          </h1>
          <p className="text-base sm:text-lg text-gray-soft mb-10 max-w-2xl mx-auto leading-relaxed">
            Transform your craft supply chaos into an organized sanctuary. 
            Get AI-powered design suggestions tailored to your collection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGoogleSignIn}
              data-testid="hero-cta-google-btn"
              className="bg-sage text-white px-10 py-4 rounded-lg text-lg font-medium hover:bg-sage-dark hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#fff" fillOpacity=".7"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff" fillOpacity=".8"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fff" fillOpacity=".6"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff" fillOpacity=".7"/>
              </svg>
              Start Free with Google
            </button>
            <button
              onClick={handleFacebookSignIn}
              data-testid="hero-cta-facebook-btn"
              className="bg-[#1877F2] text-white px-10 py-4 rounded-lg text-lg font-medium hover:bg-[#166FE5] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Start Free with Facebook
            </button>
          </div>
          {/* Email sign-in */}
          <div className="max-w-sm mx-auto mt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-charcoal/20"></div>
              <span className="text-xs text-charcoal/50">or continue with email</span>
              <div className="flex-1 h-px bg-charcoal/20"></div>
            </div>
            <MagicCodeSignIn onSuccess={() => { window.location.href = '/stash'; }} />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-light text-charcoal mb-4">Everything you need to organize your craft stash</h2>
            <p className="text-gray-soft max-w-2xl mx-auto">Powerful tools designed specifically for paper crafters</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12">
            {/* Feature 1 */}
            <div className="text-center group">
              <div className="bg-cream rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:bg-rose-light transition-colors duration-300">
                <Package size={32} className="text-sage" />
              </div>
              <h3 className="text-xl sm:text-2xl font-medium text-charcoal mb-4">Fast, Smart Item Entry</h3>
              <p className="text-gray-soft leading-relaxed">
                Snap a photo and let AI identify your product instantly, paste a product URL to auto-fill details, or add items manually. 
                Every method auto-detects colors and sentiments for powerful search later.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center group">
              <div className="bg-cream rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:bg-rose-light transition-colors duration-300">
                <Search size={32} className="text-sage" />
              </div>
              <h3 className="text-xl sm:text-2xl font-medium text-charcoal mb-4">Search by Color & Sentiment</h3>
              <p className="text-gray-soft leading-relaxed">
                Find supplies by color family, sentiment, category, brand, or keyword. 
                Color detection identifies the dominant colors in every item so you can search visually across your entire stash.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center group">
              <div className="bg-cream rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:bg-rose-light transition-colors duration-300">
                <Wand2 size={32} className="text-sage" />
              </div>
              <h3 className="text-xl sm:text-2xl font-medium text-charcoal mb-4">Craft Companion</h3>
              <p className="text-gray-soft leading-relaxed">
                Describe what you want to create and our AI recommends supplies from your actual stash — 
                matching colors, sentiments, and materials. Complete with step-by-step assembly instructions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-beige">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-light text-charcoal mb-4">Pricing That Fits Your Stash</h2>
            <p className="text-gray-soft max-w-xl mx-auto mb-4">
              Start free with 20 items. When you need more, pick a starting tier and add items as you go. Your rate automatically improves at each threshold.
            </p>
          </div>
          
          {/* How pricing works */}
          <div className="mb-10 bg-white rounded-xl p-6 border border-wood max-w-3xl mx-auto">
            <h3 className="text-lg font-medium text-charcoal mb-3 text-center">How It Works</h3>
            <div className="grid sm:grid-cols-3 gap-4 text-center">
              <div className="p-3">
                <div className="text-2xl font-light text-gold mb-1">1</div>
                <div className="text-sm font-medium text-charcoal mb-1">Start Free</div>
                <div className="text-xs text-gray-soft">20 items included as a free grant — yours forever</div>
              </div>
              <div className="p-3">
                <div className="text-2xl font-light text-gold mb-1">2</div>
                <div className="text-sm font-medium text-charcoal mb-1">Add Paid Slots</div>
                <div className="text-xs text-gray-soft">Buy item slots in flexible increments (+10, +50, +100, +500)</div>
              </div>
              <div className="p-3">
                <div className="text-2xl font-light text-gold mb-1">3</div>
                <div className="text-sm font-medium text-charcoal mb-1">Rate Improves</div>
                <div className="text-xs text-gray-soft">Your per-item rate drops as you cross tier thresholds</div>
              </div>
            </div>
          </div>

          {/* 4 Tier Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {allTiers.map((tier) => {
              const isFree = tier.tier_id === 'free';
              const totalCapacity = isFree ? 20 : tier.item_limit + 20;
              
              return (
                <div 
                  key={tier.tier_id}
                  data-testid={`pricing-tier-${tier.tier_id}`}
                  className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 relative ${
                    tier.popular ? 'border-2 border-gold ring-2 ring-gold/20 lg:scale-105' : 'border border-wood'
                  }`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gold text-white px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap">
                      Most Popular
                    </div>
                  )}
                  
                  <h3 className="text-xl font-medium text-charcoal mb-1">{tier.name}{!isFree ? ' Rate' : ''}</h3>
                  {!isFree && (
                    <p className="text-xs text-gray-soft mb-2">Starting point</p>
                  )}
                  
                  <div className="mb-3">
                    <div className="flex items-baseline">
                      <span className={`text-3xl font-light ${tier.popular ? 'text-gold' : 'text-charcoal'}`}>
                        {isFree ? 'Free' : `$${tier.price_monthly}`}
                      </span>
                      {!isFree && <span className="text-gray-soft text-sm ml-1">/mo</span>}
                    </div>
                    {tier.price_per_item > 0 && (
                      <p className="text-xs text-gray-soft mt-1">
                        ${tier.price_per_item.toFixed(4)}/item
                      </p>
                    )}
                  </div>
                  
                  <div className="mb-4 p-3 bg-cream rounded-lg text-center">
                    <div className="text-2xl font-light text-charcoal">{totalCapacity}</div>
                    <div className="text-xs text-gray-soft">total items</div>
                    {!isFree && (
                      <div className="text-xs text-sage mt-1">{tier.item_limit} paid + 20 free</div>
                    )}
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {tier.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check size={16} className="text-sage mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-gray-soft">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    onClick={handleGoogleSignIn}
                    data-testid={`pricing-cta-${tier.tier_id}`}
                    className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      tier.popular
                        ? 'bg-sage text-white hover:bg-sage-dark'
                        : 'bg-transparent border-2 border-sage text-sage-dark hover:bg-sage-light'
                    }`}
                  >
                    {tier.tier_id === 'free' ? 'Start Free' : 'Get Started'}
                  </button>
                </div>
              );
            })}
          </div>
          
          {/* Flexible Upgrades Info */}
          <div className="mt-10 bg-white rounded-xl p-6 border border-wood">
            <div className="text-center max-w-2xl mx-auto">
              <h3 className="text-lg font-medium text-charcoal mb-2">Grow At Your Own Pace</h3>
              <p className="text-gray-soft text-sm mb-4">
                Need more space? Add items in flexible increments — no tier lock-in. 
                As your paid slots cross into the next tier, your rate automatically drops for ALL items. 
                Example: Start with Starter (100 items, $2.99/mo), add 110 more, and auto-upgrade to Crafter rate ($5.24/mo for 210 items).
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {[10, 50, 100, 500].map((increment) => (
                  <span key={increment} className="px-3 py-1 bg-cream text-charcoal text-sm rounded-full">
                    +{increment} items
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-light text-charcoal mb-4">Start creating in minutes</h2>
            <p className="text-gray-soft">Three simple steps to organized crafting bliss</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-rose text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-medium">
                1
              </div>
              <h3 className="text-lg font-medium text-charcoal mb-2">Add Your Supplies</h3>
              <p className="text-gray-soft text-sm">
                Snap a photo for instant AI identification, paste a product URL to auto-fill details, or enter items manually. Colors and sentiments are detected automatically.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-gold text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-medium">
                2
              </div>
              <h3 className="text-lg font-medium text-charcoal mb-2">Search & Discover</h3>
              <p className="text-gray-soft text-sm">
                Find exactly what you need by color family, sentiment, category, or keyword. Your entire stash is searchable in ways a physical collection never could be.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-sage text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-medium">
                3
              </div>
              <h3 className="text-lg font-medium text-charcoal mb-2">Get Craft Ideas</h3>
              <p className="text-gray-soft text-sm">
                Tell the AI what you want to create — it recommends supplies from your stash, matches colors, and provides step-by-step assembly instructions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-sage">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-light text-white mb-4">Ready to organize your creative world?</h2>
          <p className="text-sage-light mb-8">
            Join thousands of paper crafters who've discovered the joy of an organized stash.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleGoogleSignIn}
              data-testid="final-cta-google-btn"
              className="bg-white text-sage px-8 py-4 rounded-lg text-lg font-medium hover:bg-cream hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 shadow-md flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Start with Google
            </button>
            <button
              onClick={handleFacebookSignIn}
              data-testid="final-cta-facebook-btn"
              className="bg-white/20 text-white border-2 border-white/40 px-8 py-4 rounded-lg text-lg font-medium hover:bg-white/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Start with Facebook
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <AppFooter />
    </div>
  );
};

export default LandingPage;
